// components/invitations/InvitationFloatingBar.js
import { useState } from 'react';
import { X, Loader2, Sparkles } from 'lucide-react';
import { auth } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';

const InvitationPopupModal = ({ onDismiss }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { addToast } = useToast();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      addToast('Please enter an email address', 'error');
      return;
    }

    if (!validateEmail(email)) {
      addToast('Please enter a valid email address', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Call your existing invitation cloud function
      const functionUrl = 'https://us-central1-semaphore-app-db378.cloudfunctions.net/sendInvitation';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          inviterName: user.displayName || 'A Semafor user',
          inviterEmail: user.email,
          personalMessage: '',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setShowSuccess(true);
        setEmail('');
        
        addToast(
          <div className="flex items-center">
            <Sparkles className="h-4 w-4 mr-2 text-yellow-500" />
            <span>Invitation sent to {email.trim()}!</span>
          </div>,
          'success'
        );

        // Auto-dismiss after success
        setTimeout(() => {
          onDismiss();
        }, 2000);

      } else {
        throw new Error(result.error || 'Failed to send invitation');
      }

    } catch (error) {
      console.error('Error sending invitation:', error);
      addToast('Failed to send invitation. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle overlay click to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  };

  // Success state
  if (showSuccess) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div 
          className="relative text-white p-6 w-96 flex flex-col justify-center items-center"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            animation: 'slideUp 0.4s ease-out'
          }}
        >
          <div className="text-center">
            <div className="mb-4">
              <Sparkles className="h-8 w-8 mx-auto text-white" />
            </div>
            <h2 className="text-xl font-bold mb-2 uppercase tracking-wider">SUCCESS!</h2>
            <p className="text-sm opacity-90">Invitation sent successfully!</p>
            <p className="text-xs opacity-75 mt-1">Your friend will receive an email shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Custom styles for animations and backdrop blur */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .popup-container {
          animation: slideUp 0.4s ease-out;
        }
        .backdrop-blur {
          backdrop-filter: blur(10px);
        }
        .glass-effect {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-input {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s;
        }
        .glass-input:focus {
          border-color: rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.15);
          outline: none;
        }
        .glass-button {
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          transition: all 0.3s;
        }
        .glass-button:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.5);
          transform: translateY(-2px);
        }
        .glass-button:active {
          transform: translateY(0);
        }
      `}</style>

      {/* Overlay - positioned in lower right */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Modal */}
        <div 
          className="popup-container relative text-white p-8 w-96 flex flex-col justify-center"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Close Button */}
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 text-white hover:opacity-70 transition-opacity text-2xl w-8 h-8 flex items-center justify-center"
            style={{ fontWeight: '300' }}
          >
            ×
          </button>

          {/* Title */}
          <h1 
            className="text-2xl font-bold mb-6 uppercase tracking-wider"
            style={{ 
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.1'
            }}
          >
            INVITE FRIENDS
          </h1>

          {/* Social Proof */}
          <div className="glass-effect p-4 mb-6 text-center">
            <div 
              className="text-3xl font-black mb-2"
              style={{ 
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                lineHeight: '1'
              }}
            >
              2.3k+
            </div>
            <div 
              className="text-sm opacity-95 uppercase tracking-wider font-medium"
              style={{ lineHeight: '1.4' }}
            >
              people already joined through invitations
            </div>
          </div>

          {/* Description */}
          <p 
            className="text-sm mb-6 opacity-95 text-center font-normal"
            style={{ lineHeight: '1.4' }}
          >
            Share Semafor with friends who could benefit from better group coordination!
          </p>

          {/* Form */}
          <form onSubmit={handleSendInvitation} className="space-y-3">
            <input
              type="email"
              placeholder="ENTER YOUR EMAIL"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="glass-input w-full px-4 py-3 text-white text-sm placeholder-gray-300"
              style={{
                fontSize: '14px',
                color: 'white'
              }}
            />
            
            <button
              type="submit"
              disabled={isLoading}
              className="glass-button w-full px-6 py-3 text-white text-sm font-bold uppercase tracking-wider relative overflow-hidden"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                  Sending...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  SUBMIT
                  <span className="ml-3 transition-transform duration-300 hover:translate-x-1">→</span>
                </div>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default InvitationPopupModal;
